import { toast as sonnerToast } from 'vue-sonner';

const truncateMessage = (message, maxLength = 150) => {
    if (typeof message !== 'string') return message;
    return message.length > maxLength ? `${message.substring(0, maxLength)}...` : message;
};

const copyToClipboard = async (text) => {
    try {
        await navigator.clipboard.writeText(text);
        sonnerToast.success('Message copied to clipboard');
    } catch (err) {
        console.error('Failed to copy text:', err);
        sonnerToast.error('Failed to copy message');
    }
};

const openContactSupport = () => {
    if (window.Pylon) {
        window.Pylon('show');
    } else {
        console.warn('Contact support not available');
    }
};

const createEnhancedOptions = (truncatedMessage, originalMessage, options = {}) => {
    if (options.action) {
        return {
            duration: options.autoClose || 4000,
            ...options
        };
    }

    const enhancedOptions = {
        duration: options.autoClose || 4000,
        description: typeof truncatedMessage === 'string' ? truncatedMessage : String(truncatedMessage),
        ...options
    };

    if (typeof originalMessage === 'string' && originalMessage.trim()) {
        enhancedOptions.action = {
            label: 'Copy',
            onClick: () => copyToClipboard(originalMessage) // Copy the full original message
        };
    }

    return enhancedOptions;
};

const createErrorToastOptions = (truncatedMessage, originalMessage, options = {}) => {
    if (options.action || options.cancel) {
        return {
            duration: options.autoClose || 8000,
            ...options
        };
    }

    const enhancedOptions = {
        duration: options.autoClose || 8000,
        description: typeof truncatedMessage === 'string' ? truncatedMessage : String(truncatedMessage),
        ...options
    };

    if (typeof originalMessage === 'string' && originalMessage.trim()) {
        enhancedOptions.action = {
            label: 'Chat',
            onClick: openContactSupport
        };

        enhancedOptions.cancel = {
            label: 'Copy',
            onClick: () => copyToClipboard(originalMessage) // Copy the full original message
        };
    }

    return enhancedOptions;
};

export const useToast = () => {
    const success = (message, options = {}) => {
        const truncatedMessage = truncateMessage(message);
        const enhancedOptions = createEnhancedOptions(truncatedMessage, message, options);
        const title = options.title || 'Success';
        return sonnerToast.success(title, enhancedOptions);
    };

    const error = (message, options = {}) => {
        const truncatedMessage = truncateMessage(message);
        const enhancedOptions = createErrorToastOptions(truncatedMessage, message, options);
        // Use title from options, or default "Error" if no title provided
        const title = options.title || 'Error';
        return sonnerToast.error(title, enhancedOptions);
    };

    const warning = (message, options = {}) => {
        const truncatedMessage = truncateMessage(message);
        const enhancedOptions = createEnhancedOptions(truncatedMessage, message, options);
        const title = options.title || 'Warning';
        return sonnerToast.warning(title, enhancedOptions);
    };

    const info = (message, options = {}) => {
        const truncatedMessage = truncateMessage(message);
        const enhancedOptions = createEnhancedOptions(truncatedMessage, message, options);
        const title = options.title || 'Info';
        return sonnerToast.info(title, enhancedOptions);
    };

    const show = (message, options = {}) => {
        const truncatedMessage = truncateMessage(message);
        const enhancedOptions = createEnhancedOptions(truncatedMessage, message, options);
        const title = options.title || 'Notification';
        return sonnerToast(title, enhancedOptions);
    };

    const dismiss = (id) => {
        if (id) {
            sonnerToast.dismiss(id);
        } else {
            sonnerToast.dismiss();
        }
    };

    return {
        success,
        error,
        warning,
        info,
        show,
        dismiss,
        warn: warning
    };
};

export const toast = {
    success: (message, options) => useToast().success(message, options),
    error: (message, options) => useToast().error(message, options),
    warning: (message, options) => useToast().warning(message, options),
    warn: (message, options) => useToast().warning(message, options),
    info: (message, options) => useToast().info(message, options),
    dismiss: (id) => useToast().dismiss(id)
};
